class CartUpsell {
  constructor() {
    this.isSyncing = false;
    this.isAdding = false;
    this.config = this.getConfig();

    if (!this.config.discountCode) return;

    this.bindEvents();
    this.syncTurmericDiscount();
  }

  getConfig() {
    const drawer = document.querySelector('cart-drawer');
    const handles = (drawer?.dataset.turmericHandles || 'organic-turmeric,new-pdp-organic-turmeric')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);

    return {
      discountCode: (drawer?.dataset.discountCode || '').trim().toUpperCase(),
      discountTitle: (drawer?.dataset.discountTitle || drawer?.dataset.discountCode || '').trim().toUpperCase(),
      turmericHandles: handles,
      turmericTitleMatch: 'Organic Turmeric Capsules',
    };
  }

  bindEvents() {
    document.addEventListener('click', this.onDocumentClick.bind(this));

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'cart-upsell' || event.source === 'cart-items') return;
        this.syncTurmericDiscount();
      });
    }
  }

  onDocumentClick(event) {
    const addButton = event.target.closest('[data-cart-upsell-add]');
    if (!addButton) return;

    event.preventDefault();
    const upsell = addButton.closest('[data-cart-upsell]');
    if (!upsell) return;

    this.addUpsellProduct(upsell, addButton);
  }

  isTurmericItem(item) {
    const handle = this.getItemHandle(item);
    if (handle && this.config.turmericHandles.includes(handle)) return true;
    return (item.title || '').includes(this.config.turmericTitleMatch);
  }

  getItemHandle(item) {
    if (item.handle) return item.handle;
    if (!item.url) return '';
    const match = item.url.match(/\/products\/([^/?]+)/);
    return match ? match[1] : '';
  }

  cartQualifiesForDiscount(cart) {
    if (!cart?.items?.length) return false;

    let hasTurmeric = false;
    let hasOther = false;

    cart.items.forEach((item) => {
      if (this.isTurmericItem(item)) {
        hasTurmeric = true;
      } else {
        hasOther = true;
      }
    });

    return hasTurmeric && hasOther;
  }

  cartHasOurDiscount(cart) {
    if (!this.config.discountCode) return false;

    const code = this.config.discountCode;
    const title = this.config.discountTitle;

    const matchesDiscount = (discountTitle) => {
      const normalized = (discountTitle || '').trim().toUpperCase();
      return normalized === code || normalized === title || normalized.includes(code);
    };

    if (cart.cart_level_discount_applications?.some((discount) => matchesDiscount(discount.title))) {
      return true;
    }

    return cart.items.some((item) => {
      if (!this.isTurmericItem(item)) return false;

      return (
        item.discounts?.some((discount) => matchesDiscount(discount.title)) ||
        item.line_level_discount_allocations?.some((allocation) =>
          matchesDiscount(allocation.discount_application?.title)
        )
      );
    });
  }

  async fetchCart() {
    const response = await fetch(`${window.routes.cart_url}.js`);
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  async updateDiscount(discountCode) {
    const response = await fetch(`${window.routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ discount: discountCode }),
    });
    if (!response.ok) throw new Error('Failed to update discount');
    return response.json();
  }

  async refreshCartDrawer() {
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems?.onCartUpdate) {
      cartDrawerItems.onCartUpdate();
      return;
    }

    const response = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
    if (!response.ok) return;

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const selectors = ['cart-drawer-items', '.cart-drawer__footer'];

    selectors.forEach((selector) => {
      const target = document.querySelector(selector);
      const source = parsed.querySelector(selector);
      if (target && source) target.replaceWith(source);
    });

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const isEmpty = !parsed.querySelector('.cart-item');
      cartDrawer.classList.toggle('is-empty', isEmpty);
    }

    if (window.CartCheckoutLock) {
      window.CartCheckoutLock.refresh();
    }
  }

  async syncTurmericDiscount() {
    if (this.isSyncing || !this.config.discountCode) return;

    this.isSyncing = true;
    if (window.CartCheckoutLock) window.CartCheckoutLock.lock();

    try {
      const cart = await this.fetchCart();
      const qualifies = this.cartQualifiesForDiscount(cart);
      const hasOurDiscount = this.cartHasOurDiscount(cart);
      let changed = false;

      if (qualifies && !hasOurDiscount) {
        await this.updateDiscount(this.config.discountCode);
        changed = true;
      } else if (!qualifies && hasOurDiscount) {
        await this.updateDiscount('');
        changed = true;
      }

      if (changed) {
        await this.refreshCartDrawer();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell' });
        }
      }
    } catch (error) {
      console.error('Cart upsell discount sync failed:', error);
    } finally {
      this.isSyncing = false;
      if (window.CartCheckoutLock) {
        window.CartCheckoutLock.unlock();
        window.CartCheckoutLock.refresh();
      }
    }
  }

  async addUpsellProduct(upsell, button) {
    if (this.isAdding) return;

    const variantId = upsell.dataset.variantId;
    if (!variantId) return;

    this.isAdding = true;
    if (window.CartCheckoutLock) window.CartCheckoutLock.lock();
    button.classList.add('loading');
    button.disabled = true;

    try {
      const addResponse = await fetch(`${window.routes.cart_add_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });

      const addResult = await addResponse.json();
      if (addResult.status) {
        throw new Error(addResult.description || 'Unable to add product');
      }

      await this.syncTurmericDiscount();
      await this.refreshCartDrawer();

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cartData: addResult });
      }
    } catch (error) {
      console.error('Cart upsell add failed:', error);
    } finally {
      this.isAdding = false;
      button.classList.remove('loading');
      button.disabled = false;
      if (window.CartCheckoutLock) {
        window.CartCheckoutLock.unlock();
        window.CartCheckoutLock.refresh();
      }
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('cart-drawer')) {
    const cartUpsell = new CartUpsell();
    window.syncCartUpsellDiscount = () => cartUpsell.syncTurmericDiscount();
  }

  document.addEventListener('submit', (event) => {
    if (event.target.id !== 'CartDrawer-Form') return;
    if (window.CartCheckoutLock?.isLocked()) {
      event.preventDefault();
    }
  });
});
